/**
 * Resolvers for the Nova GAME_API and Core API base URLs.
 *
 * Single shared implementation of the (previously duplicated) logic each
 * game's `main.ts` used to define inline. Centralizes the existing chain so
 * games, the arcade SDK bootstrap, and `EnvUtils` all consume it from
 * `@nova/network`.
 *
 * Resolution order (first match wins) — shared by both helpers:
 *   1. Per-helper `localStorage` override (only when `IS_LOCAL=true`):
 *        - `getGameApiUrl()` -> `localStorage.overrideGameApiUrl`
 *        - `getCoreApiUrl()` -> `localStorage.overrideCoreApiUrl`
 *      Used by the webApp -> game local-dev redirect flow.
 *   2. `getGameApiUrl()` only: `localStorage.GameApiUrl`, written by the
 *      webApp's `UrlService` from its game server URL. The webApp owns the
 *      Core -> game server mapping (e.g. dev envs serve the game server on
 *      `{env}.gameserver.knowledgehook.dev`, not Core's `/api/nova`), so games
 *      don't derive it from the Core URL.
 *   3. `localStorage.CoreApiUrl` (set by the webApp local-dev redirect).
 *      `getGameApiUrl()` appends `/api/nova` (Core's proxy, for webApp builds
 *      that predate `GameApiUrl`); `getCoreApiUrl()` returns it as-is.
 *   4. `CORE_API_URL` (the literal `KHWEBAPP_COREURL` placeholder, swapped
 *      per-environment at deploy time by `.github/actions/deploy-webapp`'s
 *      `update-khwebapp-values.ps1`). Same mechanism the webApp itself uses
 *      via `WebApp/src/environments/environment.cloudflare.ts`.
 *   5. Build-time fallback baked in by dotenv-webpack (used in local dev
 *      where the deploy script never runs):
 *        - `getGameApiUrl()` -> `process.env.GAME_API`
 *        - `getCoreApiUrl()` -> `process.env.CORE_API`
 *      Kept separate because the game server isn't always behind Core (dev
 *      envs serve it on its own host), so neither URL is derived from the other.
 */

const CORE_API_URL = 'KHWEBAPP_COREURL';

function trimTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

/**
 * Shared resolution for steps 3 and 4. Returns the bare core base URL
 * (no `/api/nova` suffix, no trailing slash) or `null` when neither
 * source is available.
 */
function resolveCoreBaseUrl(): string | null {
  if (typeof localStorage !== 'undefined') {
    const localCoreApiUrl = localStorage.getItem('CoreApiUrl');
    if (localCoreApiUrl) {
      return trimTrailingSlash(localCoreApiUrl);
    }
  }

  // `RegExp.prototype.test` defeats terser's constant-folding so this branch
  // survives minification; with `String.prototype.startsWith` terser would
  // evaluate the placeholder string at build time and dead-code the branch,
  // breaking the deploy-time substitution.
  if (/^https?:/.test(CORE_API_URL)) {
    return trimTrailingSlash(CORE_API_URL);
  }

  return null;
}

/**
 * Returns the Nova GAME_API base URL: the webApp's game server URL, else the
 * core URL with `/api/nova`.
 */
export function getGameApiUrl(): string {
  // NOTE: `process.env.X` must be referenced as the literal pattern so
  // webpack's DefinePlugin (via dotenv-webpack in the games' bundles) can
  // statically replace it at build time. Aliasing `const env = process.env`
  // would break that replacement and yield `undefined` at runtime.
  if (typeof localStorage !== 'undefined' && process.env.IS_LOCAL === 'true') {
    const override = localStorage.getItem('overrideGameApiUrl');
    if (override) { return override; }
  }

  if (typeof localStorage !== 'undefined') {
    const gameApiUrl = localStorage.getItem('GameApiUrl');
    if (gameApiUrl) { return trimTrailingSlash(gameApiUrl); }
  }

  const base = resolveCoreBaseUrl();
  if (base) { return `${base}/api/nova`; }

  return process.env.GAME_API ?? '';
}

/**
 * Returns the Core API base URL (no `/api/nova` suffix).
 *
 * Used by the arcade SDK so that analytics and other Core endpoints land in
 * the matching deploy environment instead of being hardcoded to a single
 * test env.
 */
export function getCoreApiUrl(): string {
  if (typeof localStorage !== 'undefined' && process.env.IS_LOCAL === 'true') {
    const override = localStorage.getItem('overrideCoreApiUrl');
    if (override) { return trimTrailingSlash(override); }
  }

  const base = resolveCoreBaseUrl();
  if (base) { return base; }

  // Builds whose env file has no `CORE_API` get dotenv-webpack's
  // `MISSING_ENV_VAR` stub, so only accept a URL.
  const coreApi = process.env.CORE_API;
  if (coreApi && /^https?:/.test(coreApi)) { return trimTrailingSlash(coreApi); }

  return '';
}
